import React, { HTMLProps } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Trans } from 'react-i18next';
import { Theme } from '@app/theme';
import { OverlayLoader, BackButton } from '@app/core';
import { InfiniteLoadingContainer } from '../InfiniteLoadingContainer';
import { UseInfiniteQueryResult } from 'react-query';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    padding: [[theme.marginBase * 8, theme.marginBase * 7]],
    minHeight: '100vh',
    [theme.media.mobile]: {
      padding: [[theme.marginBase * 8, theme.marginBase * 2]],
      minHeight: 'auto',
    },
  },
  containerWithScroll: {
    height: '100vh',
    overflowY: 'auto',
    [theme.media.mobile]: {
      heightY: 'auto',
    },
  },
  containerNoBottomPadding: {
    paddingBottom: 0,
  },
  backButton: {
    marginBottom: theme.marginBase * 3,
  },
  titleLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
    marginRight: -theme.marginBase * 3,
    '& > *': {
      marginRight: theme.marginBase * 3,
    },
    [theme.media.mobile]: {
      marginRight: -theme.marginBase * 2,
      marginTop: -theme.marginBase * 2,
      '& > *': {
        marginRight: theme.marginBase * 2,
        marginTop: theme.marginBase * 2,
      },
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  title: {
    ...theme.fonts.h3,
    marginRight: theme.marginBase * 2,
  },
}));

interface Props<TData extends {}> extends HTMLProps<HTMLDivElement> {
  banner?: React.ReactNode | React.ReactNode[];
  children?: React.ReactNode;
  title?: string;
  actions?: React.ReactNode | React.ReactNode[];
  listEmpty?: boolean;
  fetching?: boolean;
  emptyListContent?: React.ReactChild;
  scrollInside?: boolean;
  titleSideElement?: React.ReactNode | React.ReactNode[];
  backPath?: string;
  infiniteQueryResponse?: UseInfiniteQueryResult<TData>;
  loader?: React.ReactNode;
}

export const DashboardListContentPage = <TData extends {}>({
  title,
  children,
  actions,
  listEmpty,
  fetching,
  emptyListContent,
  scrollInside,
  banner,
  titleSideElement,
  backPath,
  ref,
  ...rest
}: Props<TData>) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  if (!fetching && listEmpty && emptyListContent) {
    return <React.Fragment>{emptyListContent}</React.Fragment>;
  }

  const displayLoader =
    listEmpty === undefined ? fetching : listEmpty && fetching;
  return (
    <InfiniteLoadingContainer
      {...rest}
      className={classnames(
        classes.container,
        {
          [classes.containerWithScroll]:
            scrollInside || !!rest.infiniteQueryResponse,
          [classes.containerNoBottomPadding]: scrollInside,
        },
        rest.className,
      )}
    >
      {displayLoader && <OverlayLoader transparent />}
      {banner}
      {backPath && (
        <BackButton
          to={backPath}
          color="ink"
          overGray
          className={classes.backButton}
        />
      )}
      <div className={classes.titleLine}>
        <div className={classes.content}>
          <h1 className={classes.title}>{title}</h1>
          {titleSideElement}
        </div>
        {!listEmpty && actions}
      </div>
      {!listEmpty && children}
    </InfiniteLoadingContainer>
  );
};
